"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var Setoid_1 = require("fp-ts/lib/Setoid");
var Array_1 = require("fp-ts/lib/Array");
var function_1 = require("fp-ts/lib/function");
var spel2js_1 = require("spel2js");
var getIndexOfClosingParen_1 = __importDefault(require("./getIndexOfClosingParen"));
var splitByCommasAtCurrentLevelOnly_1 = __importDefault(require("./splitByCommasAtCurrentLevelOnly"));
var getSelectionExpression_1 = require("./getSelectionExpression");
var getIndexOfNextSymbol_1 = __importDefault(require("./getIndexOfNextSymbol"));
var isPropertyNode = function (node) { return node._type === 'property'; };
var isPropertyOrCompound = function (node) {
    return node._type === 'property' || node._type === 'compound';
};
var getStringRep = function (expression) { return function (ast) {
    return expression.slice(ast.getStartPosition(), ast.getEndPosition());
}; };
var getFullExpression = function (expression) {
    var getString = getStringRep(expression);
    var getExpression = function (node) {
        var parent = node.getParent();
        if (!parent) {
            return [expression, 0, expression.length];
        }
        if (isPropertyOrCompound(parent)) {
            return getExpression(parent);
        }
        return [getString(node), node.getStartPosition(), node.getEndPosition()];
    };
    return getExpression;
};
var isMethodNode = function (node) { return node._type === 'method'; };
var isFunctionNode = function (node) { return node._type === 'function'; };
var replaceBracketedIndexesWith = function (replaceWith) { return function (path) {
    /* uses a unique identifier (_&_) to split on only the first occurrence of '[' */
    return path
        .replace(/\[/, '_&_')
        .split('_&_')
        .map(function (s) {
        return s.indexOf(']') < s.indexOf('[')
            ? // there are multiple brackets onthe same depth - continue replacing
                replaceBracketedIndexesWith(replaceWith)(s.slice(s.indexOf(']') + 1))
            : // nested brackets (we can ignore) - replace with _ALL_
                s[s.lastIndexOf(']') + 1]
                    ? s.slice(s.lastIndexOf(']') + 1)
                    : '';
    })
        .join(replaceWith);
}; };
var fieldsUsedInExpression = function (expression, 
/*
    treatSelectionAsAll - for an expression like foo.?[#this.bar],
    treatSelectionAsAll: false means we get foo.bar as an expansion
    (this is good for things like fetching data)
    treatSelectionASAll: true means we get foo._ALL_.bar
    (this is good for choosing what data to cache, or null initialize.
        e.g. if we set('foo.bar', null), that would wrongly initialize our data!)
*/
treatSelectionAsAll, outerCurrentContext) {
    if (treatSelectionAsAll === void 0) { treatSelectionAsAll = false; }
    if (outerCurrentContext === void 0) { outerCurrentContext = ''; }
    var getFuncArgs = function (ast, currentContext) {
        if (currentContext === void 0) { currentContext = outerCurrentContext; }
        var expR = expression.slice(ast.getStartPosition());
        // below exp includes literals and other expressions
        var startOfArgs = expR.indexOf('(') + 1;
        var leftAfterInitialParens = expR.slice(startOfArgs);
        // arguments at the top level (escaping nested function arguments or anything in strings)
        var args = (0, splitByCommasAtCurrentLevelOnly_1.default)(leftAfterInitialParens.slice(0, (0, getIndexOfClosingParen_1.default)(leftAfterInitialParens)));
        return args
            .filter(function (t) { return t; })
            .flatMap(function (argumentExpression) {
            return fieldsUsedInExpression(argumentExpression, treatSelectionAsAll, currentContext)(spel2js_1.SpelExpressionEvaluator.compile(argumentExpression)._compiledExpression);
        });
    };
    var getExpression = getFullExpression(expression);
    var strUniq = (0, Array_1.uniq)(Setoid_1.setoidString);
    var substituteDynamicIndexesWithAll = replaceBracketedIndexesWith('._ALL_');
    // expressionExistsInOuterExpressionScope
    // this implimentation is kind of dumb since it's just a text search, but it should be used for things like
    // #this.foo, which are unlikely to show up in strings. Anyway, it results in a false positive (less bad),
    // rather than a false negative (very bad, might not expand something we need)
    // if we must, we can implement this more complexly
    var expressionExistsInOuterExpressionScope = function (e) {
        return ((0, getSelectionExpression_1.getSelectionExpression)(expression)
            // false if there is an outer scope, and the field doesn't exist in it.
            .map(function (_a) {
            var basePath = _a.basePath, after = _a.after;
            return basePath.indexOf(e) !== -1 || after.indexOf(e) !== -1;
        })
            .getOrElse(true));
    };
    /*
        getCleanedPropertyExpression:
        strip function calls.
    */
    var getCleanedPropertyExpression = function (ast) {
        var _a = getExpression(ast), subExpression = _a[0], endIx = _a[2];
        /*
            Sometimes there are method calls in chained property expressions, e.g.
            myArray.size() => we want "myArray"
            foo.bar.contains(baz) => we wans "foo.bar"
            So lets find the first unescaped "("
            getIndexOfNextSymbol("("")
            then find the index of immediately preceding ., and remove everything after that.
            (the method node itself will be handled in ast traversal so we can ignore expressions as arguments.)
        */
        var ixOfAnyOpeningParens = (0, getIndexOfNextSymbol_1.default)('(')(subExpression);
        if (ixOfAnyOpeningParens > -1) {
            var beforeOpener = subExpression.slice(0, ixOfAnyOpeningParens);
            if (!beforeOpener.includes('.') && beforeOpener.trim().startsWith('#')) {
                // we need to handle this because spel2js registers #foo().bar as a propertyExpression!
                // #foo.bar().baz will also be caught and ignored, and that's fine.
                // We just don't want to exclude #this.foo.bar because that's a property that might be inside a selection expression.
                // e.g list.?[#this.foo.bar] hits a property list.foo.bar
                return '';
            }
            if (beforeOpener.match(/^\s*[a-zA-Z_]+[a-zA-Z_\-0-9.]*$/)) {
                // this case catches eg. foo.contains(bar)
                return beforeOpener.slice(0, beforeOpener.lastIndexOf('.'));
            }
        }
        // otherwise if our subexpression ends up being called (one of these should be true)
        else if (expression.slice(endIx, endIx + 1) === '(') {
            // this case catches, e.g. .size()
            return subExpression.slice(0, subExpression.lastIndexOf('.'));
        }
        return subExpression;
    };
    var getFields = function (rootNode) {
        var getFieldsInner = function (ast, currentContext /*<- path to what '#this' is */) {
            if (currentContext === void 0) { currentContext = outerCurrentContext; }
            var children = ast.getChildren();
            if (children.length > 0) {
                return Array_1.array.chain(children, function (c) {
                    return getFieldsInner(c, currentContext).map(substituteDynamicIndexesWithAll);
                });
            }
            if (isPropertyNode(ast)) {
                var cleanedExpression = getCleanedPropertyExpression(ast);
                if (cleanedExpression) {
                    var expressionMakesSenseInScope = function (localexp) {
                        return !currentContext // if there is no context, don't do #this expressions
                            ? !localexp.startsWith('#this')
                            : // if there is a context, and we aren't going to derive more fields in a selection, it must exist in the outer scope
                                localexp.startsWith('#this') && (0, getSelectionExpression_1.getSelectionExpression)(localexp).isNone()
                                    ? expressionExistsInOuterExpressionScope(localexp)
                                    : // there is a selectionexpression, or it's a regular old non-scoped field
                                        true;
                    };
                    var applyScope = function (e) { return (e.startsWith('#this') ? "".concat(currentContext).concat(e.slice('#this'.length)) : e.startsWith('#root.') ? e.slice('#root.'.length) : e); };
                    if (expressionMakesSenseInScope(cleanedExpression)) {
                        var scopedExpression_1 = applyScope(cleanedExpression);
                        return (0, getSelectionExpression_1.getSelectionExpression)(scopedExpression_1)
                            .map(
                        // there is a selection expression  (.?[...]) on this property-expression
                        function (_a) {
                            var basePath = _a.basePath, selectionExpression = _a.selectionExpression;
                            return fieldsUsedInExpression(selectionExpression, treatSelectionAsAll, treatSelectionAsAll ? basePath + '._ALL_' : basePath)(spel2js_1.SpelExpressionEvaluator.compile(selectionExpression)
                                ._compiledExpression)
                                .map(substituteDynamicIndexesWithAll)
                                .concat([scopedExpression_1.slice(0, scopedExpression_1.indexOf('.?['))]);
                        })
                            .getOrElse([scopedExpression_1].map(substituteDynamicIndexesWithAll));
                    }
                }
                return [];
            }
            if (isMethodNode(ast) || isFunctionNode(ast)) {
                return getFuncArgs(ast, currentContext);
            }
            return [];
        };
        return getFieldsInner(rootNode);
    };
    return (0, function_1.compose)(strUniq, 
    // remove standalone references to 'this'
    function (a) { return a.filter(function (e) { return e !== 'this'; }); }, 
    // replace safe-navigation '?.' and selection '.?' with ., and trim whitespace
    function (a) {
        return Array_1.array.map(a, function (s) {
            return s
                .split(/\.+\?\.*|\.*\?\.+/g)
                .join('.')
                .trim();
        });
    }, function (a) { return a.map(function (p) { return (p.endsWith('?') ? p.slice(0, -1) : p); }); }, getFields);
};
exports.default = fieldsUsedInExpression;
//# sourceMappingURL=index.js.map